import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ICheckoutProcessData } from 'meta/pages/checkout'
import { IPurchaseForm } from 'meta/pages/purchase'
import { ITicket, TicketAdapter } from 'meta/pages/tickets'
import { QueryParams } from 'meta/routes'
import { Routes } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { useAppDispatch } from 'store'
import { AuthActions } from 'store/auth'
import { CookieName } from 'utils/cookie'
import { CookieClient, getFastCheckoutUserObject } from 'utils/cookie/client'
import { HttpRequest } from 'utils/http'
import { generateRandomString } from 'utils/string'
import { Tokens } from 'utils/tokens'

import { checkIfTransactionIsGoodsRelated } from 'components/Pages/PurchaseForm/Tickets/utils'
import { useIsAppInitialized } from 'hooks/useApp'
import { useUserData } from 'hooks/useAuth'
import { useCheckoutProcess } from 'hooks/useCheckout'
import { useQueryParams } from 'hooks/useQueryParams'

interface GSFieldValues {
  firstName: string
  lastName: string
  phone: string
  phonePrefix: string
}

export const useHandleGSFastCheckout = () => {
  const appInitialized = useIsAppInitialized()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const checkoutProcess = useCheckoutProcess()
  const userData = useUserData()
  const {
    transactionCode,
    comingFromGSFastCheckout,
    authToken: authTokenFromQuery,
  } = useQueryParams<QueryParams['CheckoutProcess']>()

  const currentAuthToken = CookieClient.get(CookieName.TOKEN)

  const [tickets, setTickets] = useState<ITicket[]>()
  const [fieldValues, setFieldValues] = useState<GSFieldValues | null>(null)

  const shouldDoFastCheckout = (data: ICheckoutProcessData) => {
    return (
      comingFromGSFastCheckout ||
      (data?.event && data?.event?.policy && checkIfTransactionIsGoodsRelated(data?.event?.policy.code))
    )
  }

  const redirectToPurchaseForm = () => {
    if (!checkoutProcess?.data || !checkoutProcess?.data?.transactionToken) return

    let queryString = Routes.PurchaseForm.generatePath(undefined, {
      transactionCode,
      transactionToken: checkoutProcess.data.transactionToken,
    })

    const token = String(authTokenFromQuery)
    // workaround for iframe cookies not working properly
    if (Tokens.isValid(token) && !currentAuthToken) {
      dispatch(AuthActions.login({ token }))
      queryString += `&authToken=${token}`
    }

    navigate(queryString)
  }

  useEffect(() => {
    if (
      !appInitialized ||
      !checkoutProcess?.data ||
      !checkoutProcess.data.event ||
      !checkoutProcess.data.cartItems ||
      !checkoutProcess.data.transactionToken ||
      !shouldDoFastCheckout(checkoutProcess.data)
    )
      return

    const { data } = checkoutProcess
    const { transactionToken } = data

    ;(async () => {
      try {
        const { data } = await HttpRequest.get<IPurchaseForm>(
          `${ApiEndpoint.purchase.form()}?transactionCode=${transactionCode}&transactionToken=${transactionToken}`
        )

        const tickets = TicketAdapter.convertResponse(data.items, data.groups)
        setTickets(tickets)

        //@ts-expect-error error type is unknown
      } catch (e: object) {
        redirectToPurchaseForm()
      }
    })()
  }, [appInitialized, checkoutProcess])

  useEffect(() => {
    if (
      !appInitialized ||
      !checkoutProcess?.data ||
      !checkoutProcess.data.event ||
      !checkoutProcess.data.cartItems ||
      !checkoutProcess.data.transactionToken ||
      !shouldDoFastCheckout(checkoutProcess.data)
    )
      return

    if (tickets) {
      const fastCheckoutUserData = getFastCheckoutUserObject()

      if (
        (userData?.phone || fastCheckoutUserData?.phone) &&
        (userData?.phonePrefix || fastCheckoutUserData?.phonePrefix)
      ) {
        const firstName = userData?.firstName ?? 'GoodsService'
        const lastName = userData?.lastName ?? generateRandomString(12)
        const phone: string = fastCheckoutUserData?.phone ?? String(userData?.phone) ?? ''
        const phonePrefix: string = fastCheckoutUserData?.phonePrefix ?? userData?.phonePrefix ?? ''

        setFieldValues({
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          phonePrefix: phonePrefix,
        })
      }
    }
  }, [appInitialized, comingFromGSFastCheckout, checkoutProcess, tickets])

  useEffect(() => {
    if (
      !appInitialized ||
      !checkoutProcess?.data ||
      !checkoutProcess.data.event ||
      !checkoutProcess.data.cartItems ||
      !checkoutProcess.data.transactionToken ||
      !shouldDoFastCheckout(checkoutProcess.data)
    )
      return

    if (fieldValues && tickets) {
      ;(async () => {
        try {
          await HttpRequest.post<IPurchaseForm>(ApiEndpoint.purchase.form(), {
            transactionCode: transactionCode,
            transactionToken: checkoutProcess?.data?.transactionToken,
            items: tickets.map((elem) => {
              return {
                buyerData: {
                  quantity: 1,
                  firstName: fieldValues.firstName,
                  lastName: fieldValues.lastName,
                  phone: fieldValues.phone,
                  phonePrefix: fieldValues.phonePrefix,
                },
                additionalInfos: [],
                appointments: [],
                typeTicketId: elem.id,
              }
            }),
          })

          setTimeout(() => {
            const path = Routes.GoodsAndServicesTansactions.generatePath({ uuid: transactionCode })
            navigate(path)
          }, 3000)

          //@ts-expect-error error type is unknown
        } catch (e: object) {
          redirectToPurchaseForm()
        }
      })()
    }
  }, [appInitialized, comingFromGSFastCheckout, fieldValues, checkoutProcess, tickets])
}
